import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { convertFromIST, formatTimeDisplay } from '../utils/timezone';

const CalendlyStyleCalendar = ({ 
  availableSlots = [], 
  selectedDate, 
  selectedTime, 
  onSelectSlot,
  clientTimezone = 'Asia/Kolkata',
  timezoneAbbr = 'IST'
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDatesSet, setAvailableDatesSet] = useState(new Set());

  // Build a set of available dates for quick lookup
  useEffect(() => {
    const dates = new Set(availableSlots.map(slot => slot.date));
    setAvailableDatesSet(dates);
  }, [availableSlots]);

  // Get slots for the selected date
  const slotsForSelectedDate = availableSlots.filter(slot => slot.date === selectedDate);

  // Calendar helpers
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay, year, month };
  };

  const { daysInMonth, startingDay, year, month } = getDaysInMonth(currentMonth);

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const formatDateString = (day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getFullYear() === year && 
           today.getMonth() === month && 
           today.getDate() === day;
  };

  const isPastDate = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(year, month, day);
    return checkDate < today;
  };

  const isDateAvailable = (day) => {
    const dateStr = formatDateString(day);
    return availableDatesSet.has(dateStr);
  };

  const handleDateClick = (day) => {
    if (isPastDate(day) || !isDateAvailable(day)) return;
    const dateStr = formatDateString(day);
    // Clear time when selecting new date
    onSelectSlot(dateStr, null);
  };

  const handleTimeClick = (slot) => {
    onSelectSlot(selectedDate, slot.time_ist);
  };

  // Generate calendar grid
  const calendarDays = [];
  
  // Empty cells before first day
  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="h-10 md:h-12" />);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = formatDateString(day);
    const isSelected = selectedDate === dateStr;
    const available = isDateAvailable(day);
    const past = isPastDate(day);
    const today = isToday(day);
    
    calendarDays.push(
      <button
        key={day}
        onClick={() => handleDateClick(day)}
        disabled={past || !available}
        className={`
          h-10 md:h-12 rounded-full flex items-center justify-center text-sm md:text-base font-medium transition-all
          ${isSelected 
            ? 'bg-primary text-white' 
            : available && !past
              ? 'bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer'
              : past
                ? 'text-muted-foreground/40 cursor-not-allowed'
                : 'text-muted-foreground/60 cursor-not-allowed'
          }
          ${today && !isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        `}
        data-testid={`calendar-day-${day}`}
      >
        {day}
      </button>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Calendar Section */}
      <div className="flex-1">
        <div className="bg-white rounded-2xl border border-muted p-4 md:p-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={goToPrevMonth}
              className="p-2 hover:bg-muted rounded-full transition-colors"
              data-testid="prev-month"
            >
              <ChevronLeft size={20} />
            </button>
            <h3 className="text-lg md:text-xl font-heading font-medium">
              {monthNames[month]} {year}
            </h3>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-muted rounded-full transition-colors"
              data-testid="next-month"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="h-8 flex items-center justify-center text-xs md:text-sm text-muted-foreground font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-muted text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-primary/10"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-primary"></div>
              <span>Selected</span>
            </div>
          </div>
        </div>
      </div>

      {/* Time Slots Section */}
      <div className="lg:w-72 shrink-0">
        <div className="bg-white rounded-2xl border border-muted p-4 md:p-6 h-full">
          {!selectedDate ? (
            <div className="text-center py-12">
              <Clock size={40} className="mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">Select a date to see available times</p>
            </div>
          ) : (
            <>
              <h4 className="font-medium text-foreground mb-1">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h4>
              <p className="text-xs text-muted-foreground mb-4">
                {slotsForSelectedDate.length} time{slotsForSelectedDate.length !== 1 ? 's' : ''} available
                {clientTimezone !== 'Asia/Kolkata' && ` (${timezoneAbbr})`}
              </p>
              
              {slotsForSelectedDate.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">No times available</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {slotsForSelectedDate.map((slot, index) => {
                    const converted = convertFromIST(slot.date, slot.time, clientTimezone);
                    const isSelected = selectedTime === slot.time_ist;
                    
                    return (
                      <button
                        key={`${slot.date}-${slot.time_ist}`}
                        onClick={() => handleTimeClick(slot)}
                        className={`
                          w-full py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all
                          ${isSelected 
                            ? 'border-primary bg-primary text-white' 
                            : 'border-muted hover:border-primary bg-white text-foreground'
                          }
                        `}
                        data-testid={`time-slot-${slot.time_ist}`}
                      >
                        {formatTimeDisplay(converted.time)}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendlyStyleCalendar;
